/**
 * GridRenderer - Renders tiles in grid mode with row/column organization
 *
 * Grid mode displays tiles organized by rows with row headers separating them.
 * Each row represents one dimension (e.g., run, step) and tiles flow across columns.
 */

import {MediaCardType} from '@wandb/weave/common/types/media';
import {clsx} from 'clsx';
import React from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Card} from 'semantic-ui-react';

import {CustomRunNames} from '../../../../state/views/customRunNames/types';
import * as PanelTypes from '../../../../state/views/panel/types';
import {PartRefFromObjSchema} from '../../../../state/views/types';
import {MediaConfigWithDefaults} from '../../Component';
import {getMaskOptions} from '../../tiles/getTilesWithMaskOptions';
import {
  RowFooterSlot,
  RowHeaderSlot,
  TileFooterSlot,
  TileHeaderSlot,
} from '../slots';
import {GridCell, GridData, SlotRegistry} from '../types';
import {TileRenderer} from './TileRenderer';

export type GridRendererProps = {
  gridData: GridData;
  slots: SlotRegistry;
  config: MediaConfigWithDefaults;
  cardWidth: number;
  cardHeight: number;
  mediaType: MediaCardType;
  mediaSliderKey: string;
  panelRef: PartRefFromObjSchema<PanelTypes.PanelObjSchema>;
  disableRunLinks?: boolean;
  customRunNames?: CustomRunNames;
  columnCount: number;
  shrunk?: boolean;
  showActualSize?: boolean;
  allMasks?: any;
};

/**
 * Renders tiles in grid mode with rows and row headers.
 *
 * @param props - GridRendererProps
 * @returns React element
 */
export const GridRenderer: React.FC<GridRendererProps> = ({
  gridData,
  slots,
  config,
  cardWidth,
  cardHeight,
  mediaType,
  mediaSliderKey,
  panelRef,
  disableRunLinks,
  customRunNames,
  columnCount,
  shrunk,
  showActualSize,
  allMasks,
}) => {
  // Helper to expand cells with mask options into multiple tiles
  const expandCellsWithMasks = (cells: GridCell[]): GridCell[] => {
    if (!allMasks) {
      return cells;
    }

    const expandedCells: GridCell[] = [];
    for (const cell of cells) {
      if (cell.isEmpty) {
        expandedCells.push(cell);
        continue;
      }

      // Get mask options for this cell
      const maskOptionsList = getMaskOptions(config, allMasks, {
        run: cell.run,
        mediaStep: cell.step,
        mediaIndex: cell.mediaIndex,
        mediaKey: cell.mediaKey,
        runSignature: cell.tileSpec.runSignature,
      });

      // Create a cell for each mask option
      for (const maskOpts of maskOptionsList) {
        expandedCells.push({
          ...cell,
          maskOptions: maskOpts,
        });
      }
    }

    return expandedCells;
  };
  const tileHeaderControls = slots['tile-header'] || [];
  const tileFooterControls = slots['tile-footer'] || [];
  const rowHeaderControls = slots['row-header'] || [];
  const rowFooterControls = slots['row-footer'] || [];

  let tileIndex = 0;

  return (
    <div className="flex w-full flex-col">
      {gridData.rows.map((row, rowIdx) => {
        const rowKey = row.rowKey || `row-${rowIdx}`;

        // Expand cells with masks for this row only
        const expandedCells = expandCellsWithMasks(row.cells);

        // Truncate to column count instead of wrapping
        const cellsToRender = expandedCells.slice(0, columnCount);

        return (
          <React.Fragment key={rowKey}>
            <RowHeaderSlot
              controls={rowHeaderControls}
              row={row}
              disableRunLinks={disableRunLinks}
            />

            <Card.Group
              style={{
                gridTemplateColumns: `repeat(${columnCount},${(
                  100 / columnCount
                ).toFixed(2)}%)`,
                display: cellsToRender.length > 0 ? 'grid' : 'flex',
                justifyItems: 'center',
                margin: 0,
              }}
              className={clsx('media-cards items-start justify-center', {
                'media-cards--pixelated': config.pixelated,
                'media-cards--table': mediaType === 'table',
                'media-cards--shrunk': shrunk,
                'media-cards--actual': showActualSize,
                'media-cards--full': !showActualSize,
              })}>
              {cellsToRender.map((cell, cellIdx) => {
                const currentTileIndex = tileIndex++;

                return (
                  <TileRenderer
                    key={`${rowKey}-cell-${cellIdx}`}
                    cell={cell}
                    tileIndex={currentTileIndex}
                    cardWidth={cardWidth}
                    cardHeight={cardHeight}
                    config={config}
                    mediaType={mediaType}
                    mediaSliderKey={mediaSliderKey}
                    panelRef={panelRef}
                    disableRunLinks={disableRunLinks}
                    customRunNames={customRunNames}
                    tileHeaderControls={
                      <TileHeaderSlot
                        controls={tileHeaderControls}
                        cell={cell}
                        disableRunLinks={disableRunLinks}
                      />
                    }
                    tileFooterControls={
                      <TileFooterSlot
                        controls={tileFooterControls}
                        cell={cell}
                      />
                    }
                  />
                );
              })}
            </Card.Group>

            <RowFooterSlot controls={rowFooterControls} row={row} />
          </React.Fragment>
        );
      })}
    </div>
  );
};
